<template>
  <AppLayout>
    <div class="flex flex-col max-w-2xl mx-auto w-full min-h-full text-surface-0 -m-4 p-4">
      <ErrorMessage v-if="loadUsersError" message="Error loading users" @tryAgain="loadUsers" />

      <div v-else-if="loadUsersPending" class="flex justify-center items-center py-8">
        <i class="pi pi-spin pi-spinner text-2xl"></i>
        <span class="ml-2">Loading users...</span>
      </div>

      <div v-else class="mt-4">
        <div class="mb-6 flex justify-end">
          <Button @click="addUser" variant="outlined" icon="pi pi-plus" label="Add User" />
        </div>
        <div class="bg-surface-800 rounded-lg">
          <div v-if="users?.length === 0" class="p-8 text-center text-surface-400">
            No users found
          </div>
          <div v-else>
            <div
              v-for="(user, index) in users ?? []"
              :key="user.id"
              class="flex items-center justify-between p-4 hover:bg-surface-700/50 transition-colors"
              :class="{ 'border-b border-surface-700': index < (users?.length ?? 0) - 1 }"
            >
              <div class="flex-1">
                <div class="flex items-center gap-4">
                  <div class="flex-1">
                    <div class="font-medium text-surface-0">{{ user.email }}</div>
                  </div>
                  <div class="flex flex-row gap-2">
                    <span
                      v-if="user.superAdmin"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/50 text-red-300"
                    >
                      Super Admin
                    </span>
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-500/20 text-primary-300"
                    >
                      {{ user.orgRole }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="ml-4">
                <Button
                  @click="removeAccess(user.email)"
                  severity="danger"
                  variant="text"
                  size="small"
                  rounded
                  icon="pi pi-times"
                  :loading="removeAccessPending"
                  :disabled="auth.user.value?.email === user.email"
                  class="!p-2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Dialog v-model:visible="addAccessDialog" header="Add User" :style="{ width: '25rem' }" modal>
      <div class="flex items-center gap-4 mb-4">
        <label for="email" class="font-semibold w-20">Email</label>
        <InputText id="email" v-model="addAccessEmail" placeholder="Email" class="flex-auto" />
      </div>
      <template #footer>
        <Button @click="addAccessDialog = false" label="Cancel" severity="secondary" />
        <Button
          @click="addAccess"
          label="Add User"
          :disabled="addAccessEmail.length === 0"
          :loading="addAccessPending"
        />
      </template>
    </Dialog>
  </AppLayout>
</template>

<script setup lang="ts">
import type { UserDto } from '@/api/generated';
import AppLayout from '@/components/AppLayout.vue';
import ErrorMessage from '@/components/ErrorMessage.vue';
import { useApiClient } from '@/api/apiClient';
import { usePromise } from '@/composables/usePromise';
import { onMounted, ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { useAuth } from '../auth/auth';

const { api } = useApiClient();
const auth = useAuth();

const addAccessDialog = ref(false);
const addAccessEmail = ref('');

const {
  run: loadUsers,
  data: users,
  pending: loadUsersPending,
  error: loadUsersError,
} = usePromise<UserDto[]>(async () => {
  const { data, error } = await api.userControllerList();
  if (error) {
    throw new Error('Failed to load users');
  }
  return data ?? [];
});

watch(loadUsersError, () => {
  if (loadUsersError.value) {
    console.error(loadUsersError.value);
    Sentry.captureException(loadUsersError.value);
  }
});

const {
  run: addAccess,
  pending: addAccessPending,
  error: addAccessError,
} = usePromise(async () => {
  const { error } = await api.userControllerAddAccess({ body: { email: addAccessEmail.value } });
  if (error) {
    throw new Error('Failed to add user access');
  }
  addAccessDialog.value = false;
  addAccessEmail.value = '';
  await loadUsers();
});

watch(addAccessError, () => {
  if (addAccessError.value) {
    console.error(addAccessError.value);
    Sentry.captureException(addAccessError.value);
  }
});

const {
  run: removeAccess,
  pending: removeAccessPending,
  error: removeAccessError,
} = usePromise(async (email: string) => {
  const { error } = await api.userControllerRemoveAccess({ body: { email } });
  if (error) {
    throw new Error('Failed to remove user access');
  }
  await loadUsers();
});

watch(removeAccessError, () => {
  if (removeAccessError.value) {
    console.error(removeAccessError.value);
    Sentry.captureException(removeAccessError.value);
  }
});

onMounted(() => {
  loadUsers();
});

function addUser() {
  addAccessDialog.value = true;
}
</script>
